import type React from "react";
import {
  Box,
  Card,
  CardContent,
  Avatar,
  Typography,
  Button,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { motion } from "framer-motion";
import { alpha } from "@mui/material/styles";
import { useThemeColors } from "../hooks/UseThemeColors";
import { IPackage } from "../../../../types/package";
import { usePermissions } from "../../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../../constants/permissions";

interface PackageCardProps {
  pkg: IPackage;
  onEdit: (pkg: IPackage) => void;
}

const PackageCard: React.FC<PackageCardProps> = ({ pkg, onEdit }) => {
  const {
    primaryColor,
    lightPrimary,
    accentColor,
    darkText,
    whiteColor,
    neutralBackground,
  } = useThemeColors();
  const { hasPermission } = usePermissions();

  return (
    <motion.div
      whileHover={{
        y: -4,
        scale: 1.02,
        transition: { duration: 0.2 },
      }}
    >
      <Card
        sx={{
          backgroundColor: whiteColor,
          height: "100%",
          boxShadow: "0",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: `0 8px 25px ${alpha(neutralBackground, 0.25)}`,
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Header */}
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar
              sx={{
                background: `linear-gradient(135deg, ${lightPrimary}, ${alpha(
                  accentColor,
                  0.5
                )})`,
                color: accentColor,
                width: 50,
                height: 50,
                boxShadow: `0 4px 12px ${alpha(accentColor, 0.2)}`,
              }}
            >
              {pkg.icon}
            </Avatar>
            <Box flex={1}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: darkText }}
              >
                {pkg.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: alpha(darkText, 0.6), mt: 0.5 }}
              >
                {pkg.package_type}
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: primaryColor,
              }}
            >
              ${pkg.base_price}
            </Typography>
          </Box>

          {/* Description */}
          <Typography
            variant="body2"
            sx={{ color: alpha(darkText, 0.75), mb: 2 }}
            dangerouslySetInnerHTML={{ __html: pkg.description }}
          />

          {/* Divider */}
          <Divider sx={{ mb: 2 }} />

          {/* Services */}
          {pkg.base_services?.length ? (
            <Typography variant="body2" sx={{ color: darkText, mb: 2 }}>
              <strong>Includes:</strong>{" "}
              {pkg.base_services.map((s) => s.name).join(", ")}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" mb={2}>
              No base services listed
            </Typography>
          )}

          {/* Chip and Button */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Chip
              label={pkg.package_type}
              size="small"
              sx={{
                backgroundColor: alpha(primaryColor, 0.1),
                color: primaryColor,
                fontWeight: 500,
              }}
            />
            {hasPermission(PERMISSIONS.CONFIGURATION_CHANGE) && (
              <Tooltip title="Edit Package">
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => onEdit(pkg)}
                  size="small"
                  sx={{
                    textTransform: "none",
                    borderColor: primaryColor,
                    color: primaryColor,
                    borderWidth: 2,
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: alpha(primaryColor, 0.08),
                      borderColor: accentColor,
                    },
                  }}
                >
                  Edit
                </Button>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default PackageCard;
